import useInvoiceDetials from "@/hooks/Invoices/useInvoiceDetails";
import MainLayout from "@/shared/components/MainLayout";
import { Dropdown, DropdownItem } from "@/shared/components/ui/Dropdown";
import Header from "@/shared/components/ui/Header";
import { motion } from 'framer-motion';
import { ListOrdered, Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";


const InvoiceDetails = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { invoiceDetailsQuery,
        loading,
        invoice,
        total,
        displayBarcode,
        handleToggleBarcode,
        handleExportPdf
    } = useInvoiceDetials();
    return (
        <MainLayout>
            <Header title={`${t('Invoice')}`} info={invoiceDetailsQuery} />

            <main className="px-4 py-8 min-h-[calc(100vh-100px)] flex flex-col items-center justify-center">
                <div className="w-full max-w-6xl">

                    {loading ? (
                        <div className="flex justify-center py-20">
                            <Loader />
                        </div>
                    ) : (
                        <div className="bg-white rounded-[1rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
                            <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center text-white shadow-sm">
                                        <ul className="m-0 p-0"><ListOrdered size={18} /></ul>
                                    </div>
                                    <h2 className="font-black text-slate-800 tracking-tight">{t("Invoice")}</h2>
                                </div>
                                <div>
                                    <button
                                        onClick={handleToggleBarcode}
                                        className="me-2 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-slate-600">
                                        {displayBarcode ? t("hide barcode") : t("display barcode")}
                                    </button>
                                    <button
                                        onClick={handleExportPdf}
                                        className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-slate-600">
                                        {t("export pdf")}
                                    </button>
                                </div>

                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-right border-collapse">
                                    <thead>
                                        <tr className="text-slate-400 uppercase text-[11px] font-black tracking-widest border-b border-slate-100 bg-slate-50/30">
                                            <th className="px-6 py-4">{t("item_id")}</th>
                                            <th className="px-6 py-4">{t("item_name")}</th>
                                            {displayBarcode && (<th className="px-6 py-4">{t("barcode")}</th>)}
                                            <th className="px-6 py-4">{t("item_quantity")}</th>
                                            <th className="px-6 py-4">{t("item_price")}</th>
                                            <th className="px-6 py-4">{t("total")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {invoice.length > 0 ? (
                                            invoice.map((item: any, index: number) => {
                                                const isInitialBalance = item?.invoice_type?.toLowerCase() === "old";
                                                return (
                                                    <motion.tr
                                                        key={index}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: index * 0.03 }}
                                                        className={`hover:bg-violet-50/30 transition-colors group ${isInitialBalance ? "bg-slate-50 font-bold text-violet-700" : ""}`}
                                                    >
                                                        <td className="px-6 py-5 text-sm font-black text-slate-700">
                                                            <Dropdown label={
                                                                <span className="cursor-pointer hover:text-violet-600 transition-colors">
                                                                    {item?.item_id}
                                                                </span>
                                                            }>
                                                                <DropdownItem onClick={() => navigate(`https://w-itm.ntelgnt.com/item_details/${item?.item_id}`)}>
                                                                    {t("view_item")}
                                                                </DropdownItem>
                                                                <DropdownItem onClick={() => navigate(`https://w-itm.ntelgnt.com/item_movements/${item?.item_id}/${item?.item_name}`)}>
                                                                    {t("view_item_movements")}
                                                                </DropdownItem>
                                                            </Dropdown>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-slate-600">
                                                            {item?.item_name || "---"}
                                                        </td>
                                                        {displayBarcode && (
                                                            <td className="px-6 py-4 text-sm text-slate-600">
                                                                {item?.barcode || "---"}
                                                            </td>
                                                        )}
                                                        <td className="px-6 py-4 text-sm text-slate-500">
                                                            {item?.item_quantity}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-slate-500">
                                                            {item?.item_price || "---"}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-slate-500">
                                                            {Number(item?.item_price) * Number(item?.item_quantity) || "-"}
                                                        </td>

                                                    </motion.tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="px-6 py-20 text-center">
                                                    <div className="flex flex-col items-center gap-3 opacity-20">
                                                        <ListOrdered size={48} />
                                                        <p className="font-black uppercase tracking-widest text-sm">{t("No Data Found")}</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    {invoice?.length > 0 && (
                                        <tfoot className="bg-slate-50/80 border-t-2 border-slate-100">
                                            <motion.tr
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className={`font-black text-slate-800`}
                                            >
                                                <td colSpan={displayBarcode ? 5 : 4} className="px-6 py-5 text-sm uppercase tracking-wider">
                                                    {t("Total Amount")}
                                                </td>
                                                <td className="px-6 py-5 text-sm text-violet-700 text-right bg-violet-50/50">
                                                    {total}
                                                </td>
                                            </motion.tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </MainLayout>
    );
}

export default InvoiceDetails